import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { SDKStudy } from '@axon/rosetta-sdk';
import { LibraryEndpoint, LibraryResourceStatus } from 'libs.react';
import { ApiClientService, useApiClient } from 'libs.react_internal';
const parseStudyConfigMetadata = (config) => ({
    ...config,
    ...(typeof config.metadata === 'string' && { metadata: JSON.parse(config.metadata) }),
});
const extractReportsFromStudyConfig = (studyConfig) => {
    if (!studyConfig?.data) {
        return [];
    }
    try {
        const study = SDKStudy.fromRosetta(studyConfig.data);
        return study.reports ?? [];
    }
    catch (error) {
        // Falling back to [] keeps the UI usable when an old config can't be parsed,
        // but log so we don't silently swallow regressions.
        console.error('[useVersionedReports] Failed to extract reports from study config', error);
        return [];
    }
};
export const useVersionedReports = (studyId, studyConfigVersion) => {
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const { data: studyConfig, isLoading, error, } = useQuery({
        queryKey: ['study_config', studyId, 'version', studyConfigVersion],
        queryFn: async () => {
            const searchParams = new URLSearchParams({
                studyId: String(studyId),
            });
            searchParams.append('status', LibraryResourceStatus.APPROVED);
            searchParams.append('status', LibraryResourceStatus.PUBLISHED);
            const { data: response } = await libraryClient.get(`${LibraryEndpoint.GET_STUDY_CONFIGS}?${searchParams.toString()}`);
            const matchingConfig = response.data
                .map(parseStudyConfigMetadata)
                .find((config) => config.version === studyConfigVersion);
            return matchingConfig ?? null;
        },
        enabled: !!studyId && studyConfigVersion != null,
    });
    const reports = useMemo(() => extractReportsFromStudyConfig(studyConfig ?? undefined), [studyConfig]);
    return { reports, isLoading, error, studyConfig };
};
